import { AjaxError } from "rxjs/ajax";
import * as _ from "lodash";

import { Action, createAsyncActions, Nullable } from "@ctra/utils";

import { FarmEntity } from "../farms";
import { DataDescriptorEntity } from "../data-dictionary";

import {
  ChartEntity,
  ChartFilters,
  ChartDataSourceType,
  ChartTimePeriod,
  ChartData,
  ChartDataSource,
  UnitSystemParam,
  CorrelationsResponse,
  CorrelationsIndexResponse,
  ShareChartPayload,
  ShareChartResponse,
  ISODuration
} from "./typings";

import types from "./types";
import { makeChartHash, normalizeChartData } from "./utils";

/**
 * Payload type for the FETCH_IFRAME_URL.pending action.
 */
export type FetchChartDataPendingPayload = {
  /**
   * auto-generated chart hash
   */
  hash: string;
  /**
   * chart id
   */
  chartID: ChartEntity["id"];
  /**
   * chart data source type
   */
  sourceType: ChartDataSourceType;
  /**
   * time unit
   */
  bucketSize: ISODuration;
  /**
   * chart time period
   */
  timePeriod: ChartTimePeriod;
  /**
   * farm ids
   */
  farmIDs: Array<FarmEntity["id"]>;
  /**
   * Unitsystem
   */
  unitSystem: UnitSystemParam;
  /**
   * chart filters
   */
  filters?: ChartFilters;
  /**
   * data url to call (if there is any)
   */
  dataURL?: string;
  /**
   * zero rendering interval to fill the gaps when needed
   */
  zeroRenderingInterval?: ISODuration;
};

/**
 * Payload type for the FETCH_IFRAME_URL.fulfilled action.
 */
export type FetchChartDataFulfilledPayload = {
  /**
   * chart id
   */
  chartID: ChartEntity["id"];
  /**
   * auto-generated chart hash
   */
  hash: string;
  /**
   * Chart data
   */
  data: ChartData;
  /**
   * Chart request URL
   */
  requestURL: Nullable<string>;
};

/**
 * Payload type for the FETCH_IFRAME_URL.rejected action.
 */
export type FetchChartDataRejectedPayload = {
  /**
   * chart id
   */
  chartID: ChartEntity["id"];
  /**
   * auto-generated chart hash
   */
  hash: string;
  /**
   * Error string for rejection
   */
  error: AjaxError;
};

/**
 * Fetch a chart data
 * @category Action
 */
const fetchChartData = createAsyncActions(
  types.FETCH_CHART_DATA,
  (
    chartID: ChartEntity["id"],
    sourceType: ChartDataSourceType,
    timePeriod: ChartTimePeriod,
    bucketSize: ISODuration,
    farmIDs: Array<FarmEntity["id"]>,
    unitSystem: UnitSystemParam,
    filters?: ChartFilters,
    dataURL?: string,
    ...rest: Array<unknown>
  ): FetchChartDataPendingPayload => {
    const metadata = _.last(rest);

    const payload: FetchChartDataPendingPayload = {
      hash: makeChartHash(chartID, timePeriod, bucketSize, farmIDs, unitSystem, filters),
      chartID,
      sourceType,
      timePeriod,
      bucketSize,
      farmIDs,
      unitSystem,
      filters,
      dataURL
    };

    if (metadata && _.isPlainObject(metadata)) {
      const zeroRenderingInterval = _.get(metadata, "zeroRenderingInterval");

      if (zeroRenderingInterval) {
        payload.zeroRenderingInterval = zeroRenderingInterval;
      }
    }

    return payload;
  },
  (
    chartID: ChartEntity["id"],
    hash: string,
    response: ChartDataSource,
    meta: {
      requestURL: Nullable<string>;
      zeroRenderingInterval: Nullable<ISODuration>;
      timePeriod: Nullable<ChartTimePeriod>;
    } = {
      requestURL: null,
      zeroRenderingInterval: null,
      timePeriod: null
    }
  ): FetchChartDataFulfilledPayload => ({
    chartID,
    hash,
    data: normalizeChartData(response, {
      timePeriod: meta.timePeriod,
      zeroRenderingInterval: meta.zeroRenderingInterval
    }),
    requestURL: meta.requestURL
  }),
  (chartID: ChartEntity["id"], hash: string, error: AjaxError): FetchChartDataRejectedPayload => ({
    chartID,
    hash,
    error
  })
);

/**
 * Fetch farm correlations
 * @category Action
 */
const fetchFarmCorrelations = createAsyncActions(
  types.FETCH_FARM_CORRELATIONS,
  (farmID: FarmEntity["id"]) => ({
    farmID
  }),
  (farmID: FarmEntity["id"], response: CorrelationsIndexResponse) => ({
    farmID,
    intervals: response.intervalDays
  }),
  (farmID: FarmEntity["id"], error: AjaxError) => ({ farmID, error })
);

/**
 * Fetch chart correlations
 * @category Action
 */
const fetchChartCorrelations = createAsyncActions(
  types.FETCH_CHART_CORRELATIONS,
  (
    farmID: FarmEntity["id"],
    dataDescriptorID: DataDescriptorEntity["id"],
    excludedDataDescriptorIDList?: DataDescriptorEntity["id"][]
  ) => ({
    farmID,
    dataDescriptorID,
    excludedDataDescriptorIDList
  }),
  (farmID: FarmEntity["id"], response: CorrelationsResponse) => ({
    farmID,
    intervals: response.intervalDays
  }),
  (farmID: FarmEntity["id"], error) => ({ farmID, error })
);

/**
 * Share a chart publicly
 * @type {AsyncActionCreator<(payload: ShareChartPayload) => ShareChartPayload, (response: ShareChartResponse) => ShareChartResponse, (error: string, statusCode: number, details: string) => {error: string, statusCode: number, details: string}>}
 */
const shareChart = createAsyncActions(
  types.SHARE_CHART,
  (payload: ShareChartPayload & { hash: string }) => payload,
  (response: ShareChartResponse & { hash: string }) => response,
  (
    hash: string,
    error: string,
    statusCode: number,
    details: string
  ): { error: string; statusCode: number; details: string; hash: string } => ({
    hash,
    error,
    statusCode,
    details
  })
);

/**
 * Update a shared chart
 * @type {AsyncActionCreator<(payload: Partial<ShareChartPayload>) => Partial<ShareChartPayload>, (response: ShareChartResponse) => ShareChartResponse, (error: string, statusCode: number, details: string) => {error: string, statusCode: number, details: string}>}
 */
const updateSharedChart = createAsyncActions(
  types.UPDATE_SHARED_CHART,
  (payload: Partial<ShareChartPayload> & { id: string; hash: string }) => payload,
  (response: ShareChartResponse & { hash: string }) => response,
  (
    hash: string,
    error: string,
    statusCode: number,
    details: string
  ): { error: string; statusCode: number; details: string; hash: string } => ({
    hash,
    error,
    statusCode,
    details
  })
);

/**
 * Load a shared chart
 * @type {AsyncActionCreator<(shortID: string) => {shortID: string}, (response: ShareChartResponse) => ShareChartResponse, (error: string, statusCode: number, details: string) => {error: string, statusCode: number, details: string}>}
 */
const fetchSharedChart = createAsyncActions(
  types.FETCH_SHARED_CHART,
  (shortID: string) => ({ shortID }),
  (response: ShareChartResponse) => response,
  (
    error: string,
    statusCode: number,
    details: string
  ): { error: string; statusCode: number; details: string } => ({
    error,
    statusCode,
    details
  })
);

/**
 * Fetch attachment from a shared chart
 * @type {AsyncActionCreator<(url: string) => {url: string}, (response: Record<string, unknown>) => Record<string, unknown>, (error: string, statusCode: number, details: string) => {error: string, statusCode: number, details: string}>}
 */
const fetchSnapshotData = createAsyncActions(
  types.FETCH_SNAPSHOT_DATA,
  (url: string) => ({ url }),
  (response: Record<string, unknown>) => response,
  (
    error: string,
    statusCode: number,
    details: string
  ): { error: string; statusCode: number; details: string } => ({
    error,
    statusCode,
    details
  })
);

/**
 * Dump chart data
 * @returns {Action}
 */
const dumpChartData = (): Action => ({
  type: types.DUMP_CHART_DATA,
  payload: {}
});

/**
 * Add chart to overlay
 * @param {ChartEntity["id"]} id
 * @returns {Action}
 */
const addToOverlay = (id: ChartEntity["id"]): Action => ({
  type: types.ADD_TO_OVERLAY,
  payload: {
    id
  }
});

/**
 * Remove chart from overlay
 * @param {ChartEntity["id"]} id
 * @returns {Action}
 */
const removeFromOverlay = (id: ChartEntity["id"]): Action => ({
  type: types.REMOVE_FROM_OVERLAY,
  payload: {
    id
  }
});

export default {
  fetchChartData,
  fetchFarmCorrelations,
  fetchChartCorrelations,
  shareChart,
  updateSharedChart,
  fetchSharedChart,
  fetchSnapshotData,
  dumpChartData,
  addToOverlay,
  removeFromOverlay
};
